// src/pages/ProductGalleryPage.jsx
import React, { useState, useMemo, useContext, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack,
  IconButton,
  useTheme,
  alpha,
  Paper,
  Drawer,
  Badge,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Zoom,
  Slide,
  Dialog,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  ShoppingBag as ShoppingBagIcon,
  Diamond as DiamondIcon,
  Scale as ScaleIcon,
  LocalOffer as LocalOfferIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Share as ShareIcon,
  Whatshot as WhatshotIcon,
  FilterAlt as FilterAltIcon,
  ClearAll as ClearAllIcon,
  Verified as VerifiedIcon,
  Inventory2 as Inventory2Icon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { DataContext } from '../../context/DataContext';

// ==================== External Components (Memoized) ====================

// Hero Section Component
const HeroSection = React.memo(({ isDark, theme }) => (
  <motion.div
    initial={{ opacity: 0, y: -30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
  >
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, sm: 3, md: 4 },
        mb: 3,
        borderRadius: '32px',
        textAlign: 'center',
        background: isDark
          ? `linear-gradient(135deg, ${alpha('#1e293b', 0.8)} 0%, ${alpha('#0f172a', 0.6)} 100%)`
          : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8fafc', 0.7)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(isDark ? '#60a5fa' : '#2563eb', 0.2)}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 70%)`,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.15)} 0%, transparent 70%)`,
        },
      }}
    >
      <Typography
        variant="h1"
        sx={{
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
          fontWeight: 800,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 2,
        }}
      >
        Discover Timeless Elegance
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: isDark ? '#cbd5e1' : '#475569',
          maxWidth: 600,
          mx: 'auto',
          fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
        }}
      >
        Explore our exquisite collection of handcrafted jewelry, each piece tells a unique story of craftsmanship and beauty
      </Typography>
    </Paper>
  </motion.div>
));

// Product Card Component
const ProductCard = React.memo(({ 
  product, 
  index, 
  formatWeight, 
  handleWhatsAppOrder, 
  handleShare, 
  openQuickView, 
  favorites, 
  toggleFavorite, 
  isDark, 
  theme 
}) => {
  const imageUrl = product.imageUrl || 'https://via.placeholder.com/400x400?text=Premium+Jewelry';
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
        whileHover={{ y: -4 }}
      >
        <Card
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            position: 'relative',
            borderRadius: '24px',
            overflow: 'hidden',
            cursor: 'pointer',
            background: isDark
              ? `linear-gradient(135deg, ${alpha('#1e293b', 0.8)} 0%, ${alpha('#0f172a', 0.6)} 100%)`
              : `linear-gradient(135deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8fafc', 0.7)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(isDark ? '#60a5fa' : '#2563eb', isHovered ? 0.4 : 0.1)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Image Section */}
          <Box sx={{ position: 'relative', pt: '100%', overflow: 'hidden', flexShrink: 0 }}>
            {!imageLoaded && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
            <CardMedia
              component="img"
              image={imageUrl}
              alt={product.name}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: imageLoaded ? 1 : 0,
                transform: isHovered ? 'scale(1.08)' : 'scale(1)',
              }}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/400x400?text=Premium+Jewelry';
                setImageLoaded(true);
              }}
            />
            
            {/* Overlay on hover */}
            <AnimatePresence>
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => openQuickView(product)}
                    sx={{
                      borderRadius: '30px',
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      transform: 'scale(0.9)',
                      animation: 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(0.9)' },
                        '50%': { transform: 'scale(1)' },
                        '100%': { transform: 'scale(0.9)' },
                      },
                    }}
                  >
                    Quick View
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Badges */}
            <Chip
              label={product.metal}
              size="small"
              icon={product.metal === 'Gold' ? <WhatshotIcon /> : <DiamondIcon />}
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                background: product.metal === 'Gold'
                  ? `linear-gradient(135deg, #f59e0b, #d97706)`
                  : `linear-gradient(135deg, #94a3b8, #64748b)`,
                color: 'white',
                fontWeight: 700,
                '& .MuiChip-icon': { color: 'white' },
              }}
            />
          </Box>
          
          {/* Content Section - Fixed minimum height */}
          <CardContent sx={{ 
            p: 2.5, 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '170px', // Fixed minimum height
          }}>
            <Typography
              variant="caption"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5,
              }}
            >
              {product.name}
            </Typography>
            
            {/* Removed Price Section */}
            
            <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ScaleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatWeight(product.weight)}
                </Typography>
              </Box>
              {product.makingCharges && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocalOfferIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {product.makingCharges}%
                  </Typography>
                </Box>
              )}
            </Stack>
            
            {/* HUID Chip with fixed height container */}
            <Box sx={{ minHeight: '32px' }}>
              {product.huid && (
                <Chip
                  label={`HUID: ${product.huid}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '10px',
                    height: '26px',
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  }}
                />
              )}
            </Box>
          </CardContent>
          
          {/* Actions - Fixed at bottom */}
          <Box sx={{ p: 2.5, pt: 0 }}>
            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={() => handleWhatsAppOrder(product)}
                sx={{
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
                  },
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Enquire
              </Button>

              <IconButton
                onClick={() => handleShare(product)}
                sx={{
                  borderRadius: '12px',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <ShareIcon />
              </IconButton>
            </Stack>
          </Box>
        </Card>
      </motion.div>
    </Grid>
  );
});

// Filter Sidebar Component
const FilterSidebar = React.memo(({ 
  selectedCategory, 
  setSelectedCategory, 
  selectedMetal, 
  setSelectedMetal, 
  priceRange, 
  setPriceRange, 
  categories, 
  metals, 
  minMaxPrice, 
  formatPrice, 
  clearAllFilters, 
  theme 
}) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterAltIcon /> Filters
      </Typography>
      <Tooltip title="Clear all">
        <IconButton onClick={clearAllFilters} size="small">
          <ClearAllIcon />
        </IconButton>
      </Tooltip>
    </Box>
    
    <Accordion defaultExpanded sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600 }}>Categories</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={selectedCategory === 'all'} onChange={() => setSelectedCategory('all')} />}
            label="All Categories"
          />
          {categories.map(cat => (
            <FormControlLabel
              key={cat}
              control={<Checkbox checked={selectedCategory === cat} onChange={() => setSelectedCategory(cat)} />}
              label={cat}
            />
          ))}
        </FormGroup>
      </AccordionDetails>
    </Accordion>
    
    <Divider sx={{ my: 1 }} />
    
    <Accordion defaultExpanded sx={{ bgcolor: 'transparent', boxShadow: 'none', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ fontWeight: 600 }}>Metal Type</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox checked={selectedMetal === 'all'} onChange={() => setSelectedMetal('all')} />}
            label="All Metals"
          />
          {metals.map(metal => (
            <FormControlLabel
              key={metal}
              control={<Checkbox checked={selectedMetal === metal} onChange={() => setSelectedMetal(metal)} />}
              label={metal}
            />
          ))}
        </FormGroup>
      </AccordionDetails>
    </Accordion>
  </Box>
));

// Quick View Modal Component
const QuickViewModal = React.memo(({ 
  quickViewOpen, 
  setQuickViewOpen, 
  selectedProduct, 
  formatWeight, 
  handleWhatsAppOrder, 
  handleShare, 
  isDark, 
  theme 
}) => (
  <Dialog
    open={quickViewOpen}
    onClose={() => setQuickViewOpen(false)}
    maxWidth="md"
    fullWidth
    TransitionComponent={Zoom}
    PaperProps={{
      sx: {
        borderRadius: '32px',
        overflow: 'hidden',
        bgcolor: isDark ? alpha('#1e293b', 0.98) : alpha('#ffffff', 0.98),
        backdropFilter: 'blur(20px)',
        boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
        maxHeight: '90vh',
      },
    }}
  >
    {selectedProduct && (
      <>
        {/* Header with Close Button */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2.5,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}>
          <Typography variant="h6" fontWeight={700}>
            Product Details
          </Typography>
          <IconButton 
            onClick={() => setQuickViewOpen(false)}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          p: 3,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: alpha(theme.palette.primary.main, 0.1),
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.primary.main, 0.3),
            borderRadius: '3px',
            '&:hover': {
              background: alpha(theme.palette.primary.main, 0.5),
            },
          },
        }}>
          
          {/* Main Content Grid */}
          <Grid container spacing={4}>
            {/* Left Column - Fixed Image Box */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  width: 400,
                  height: 400,
                  borderRadius: 3,
                  overflow: 'hidden',
                  position: 'relative',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  flexShrink: 0,
                }}
              >
                <img
                  src={selectedProduct.imageUrl || 'https://via.placeholder.com/400x400?text=Premium+Jewelry'}
                  alt={selectedProduct.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'flex',
                  }}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x400?text=Premium+Jewelry';
                  }}
                />
                
                {/* Metal Badge */}
                <Chip
                  label={selectedProduct.metal}
                  size="medium"
                  icon={selectedProduct.metal === 'Gold' ? <WhatshotIcon /> : <DiamondIcon />}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    background: selectedProduct.metal === 'Gold'
                      ? `linear-gradient(135deg, #f59e0b, #d97706)`
                      : `linear-gradient(135deg, #94a3b8, #64748b)`,
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '14px',
                    '& .MuiChip-icon': { color: 'white' },
                    zIndex: 1,
                  }}
                />
              </Box>
            </Grid>

            {/* Right Column - Product Info */}
            <Grid item xs={12} md={6}>
              <Box sx={{ width: 350 }}>
                <Typography 
                  variant="overline" 
                  sx={{ 
                    color: 'primary.main', 
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    display: 'block'
                  }}
                >
                  {selectedProduct.name}
                </Typography>
                
                {/* Removed Price Section */}

                <Divider sx={{ my: 2 }} />

                {/* Product Details */}
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      flex: 1,
                      p: 1.5, 
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ScaleIcon fontSize="small" /> Weight
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {formatWeight(selectedProduct.weight)}
                      </Typography>
                    </Box>
                    
                    {selectedProduct.grossWeight && (
                      <Box sx={{ 
                        flex: 1,
                        p: 1.5, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ScaleIcon fontSize="small" /> Gross Weight
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {formatWeight(selectedProduct.grossWeight)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {selectedProduct.makingCharges && (
                      <Box sx={{ 
                        flex: 1,
                        p: 1.5, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocalOfferIcon fontSize="small" /> making charges
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {selectedProduct.makingCharges}%
                        </Typography>
                      </Box>
                    )}
                    
                    {selectedProduct.wastage && (
                      <Box sx={{ 
                        flex: 1,
                        p: 1.5, 
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 2,
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocalOfferIcon fontSize="small" /> Wastage
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {selectedProduct.wastage}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Stack>
                
                {/* HUID Section */}
                {selectedProduct.huid && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderRadius: 2,
                    my: 2
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VerifiedIcon fontSize="small" color="primary" /> HUID
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="primary.main">
                      {selectedProduct.huid}
                    </Typography>
                  </Box>
                )}
                
                {/* Serial Number */}
                {selectedProduct.serialNo && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 2,
                    mb: 2,
                    mt: 1
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Inventory2Icon fontSize="small" /> Serial Number
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedProduct.serialNo}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Action Buttons */}
                <Stack direction="row" spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => {
                      handleWhatsAppOrder(selectedProduct);
                      setQuickViewOpen(false);
                    }}
                    sx={{
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
                      },
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 1.2,
                    }}
                  >
                    Enquire Now
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ShareIcon />}
                    onClick={() => handleShare(selectedProduct)}
                    sx={{
                      borderRadius: 3,
                      borderWidth: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { borderWidth: 2 },
                    }}
                  >
                    Share
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </>
    )}
  </Dialog>
));

// ==================== Main Component ====================

const ProductGalleryPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { stock = [], companyDetails } = useContext(DataContext);
  
  // State for filters and sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMetal, setSelectedMetal] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 500000]);
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [favorites, setFavorites] = useState([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  
  const itemsPerPage = 12;
  
  // Get shop name from company details
  const shopName = companyDetails?.[0]?.ShopName || 'Our Jewelry Store';
  const contactNumber = companyDetails?.[0]?.Phone || '919870480017';
  
  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('productFavorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);
  
  // Get unique categories and metals from stock
  const categories = useMemo(() => {
    const cats = new Set();
    stock.forEach(item => {
      if (item.name && item.sold !== "Yes") {
        cats.add(item.name);
      }
    });
    return Array.from(cats).sort();
  }, [stock]);
  
  const metals = useMemo(() => {
    const met = new Set();
    stock.forEach(item => {
      if (item.metal && item.sold !== "Yes") {
        met.add(item.metal);
      }
    });
    return Array.from(met).sort();
  }, [stock]);
  
  // Get min and max price for slider
  const minMaxPrice = useMemo(() => {
    const prices = stock
      .filter(item => item.sold !== "Yes" && item.price)
      .map(item => parseFloat(item.price) || 0);
    const min = Math.min(...prices, 0);
    const max = Math.max(...prices, 500000);
    return { min, max };
  }, [stock]);
  
  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let products = stock.filter(item => item.sold !== "Yes");
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      products = products.filter(item =>
        item.name?.toLowerCase().includes(searchLower) ||
        item.serialNo?.toString().includes(searchLower) ||
        item.metal?.toLowerCase().includes(searchLower) ||
        item.huid?.toLowerCase().includes(searchLower)
      );
    }
    
    if (selectedCategory !== 'all') {
      products = products.filter(item => item.name === selectedCategory);
    }
    
    if (selectedMetal !== 'all') {
      products = products.filter(item => item.metal === selectedMetal);
    }
    
    products = products.filter(item => {
      const price = parseFloat(item.price) || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    products = [...products].sort((a, b) => {
      switch (sortBy) {
        case 'price_asc':
          return (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0);
        case 'price_desc':
          return (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0);
        case 'weight_asc':
          return (parseFloat(a.weight) || 0) - (parseFloat(b.weight) || 0);
        case 'weight_desc':
          return (parseFloat(b.weight) || 0) - (parseFloat(a.weight) || 0);
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        default:
          return 0;
      }
    });
    
    return products;
  }, [stock, searchTerm, selectedCategory, selectedMetal, priceRange, sortBy]);
  
  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredProducts.slice(start, end);
  }, [filteredProducts, page]);
  
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedCategory, selectedMetal, priceRange, sortBy]);
  
  // Format price to Indian currency
  const formatPrice = useCallback((price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  }, []);
  
  // Format weight
  const formatWeight = useCallback((weight) => {
    if (!weight) return '0 gm';
    return `${parseFloat(weight).toFixed(3)} gm`;
  }, []);
  
  // Generate WhatsApp message
  const generateWhatsAppMessage = useCallback((product) => {
    const message = `*🛍️ Product Inquiry from ${shopName}*%0A%0A` +
      `*📦 Product Details:*%0A` +
      `━━━━━━━━━━━━━━━━━━━━%0A` +
      `🔸 *Item:* ${product.name || 'N/A'}%0A` +
      `🔸 *Serial No:* ${product.serialNo || 'N/A'}%0A` +
      `🔸 *Metal:* ${product.metal || 'N/A'}%0A` +
      `🔸 *Weight:* ${formatWeight(product.weight)}%0A` +
      `🔸 *Gross Weight:* ${formatWeight(product.grossWeight)}%0A` +
      `🔸 *Making Charges:* ${product.makingCharges || '0'}%%0A` +
      `🔸 *HUID:* ${product.huid || 'N/A'}%0A` +
      `━━━━━━━━━━━━━━━━━━━━%0A%0A` +
      `*💬 Message:* I'm interested in this product. Please provide more details.`;
    
    return message;
  }, [shopName, formatWeight]);
  
  // Handle WhatsApp order
  const handleWhatsAppOrder = useCallback((product) => {
    const message = generateWhatsAppMessage(product);
    const whatsappUrl = `https://wa.me/${contactNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
  }, [contactNumber, generateWhatsAppMessage]);
  
  // Share product
  const handleShare = useCallback(async (product) => {
    const shareData = {
      title: `${product.name} - ${shopName}`,
      text: `Check out this beautiful ${product.name} weighing ${formatWeight(product.weight)} at ${shopName}!`,
      url: window.location.href,
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(`${shareData.text}\nPrice: ${formatPrice(product.price)}`);
      alert('Product details copied to clipboard!');
    }
  }, [shopName, formatWeight, formatPrice]);
  
  // Toggle favorite
  const toggleFavorite = useCallback((productId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId];
      localStorage.setItem('productFavorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  }, []);
  
  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedMetal('all');
    setPriceRange([minMaxPrice.min, minMaxPrice.max]);
    setSortBy('newest');
  }, [minMaxPrice.min, minMaxPrice.max]);
  
  // Open quick view
  const openQuickView = useCallback((product) => {
    setSelectedProduct(product);
    setQuickViewOpen(true);
  }, []);
  
  return (
    <Box sx={{ minHeight: '100vh', position: 'relative' }}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Hero Section */}
        <HeroSection isDark={isDark} theme={theme} />
        
        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Sidebar - Desktop */}
          <Grid item xs={12} md={3}>
            <Paper
              sx={{
                p: 2.5,
                position: 'sticky',
                top: 24,
                borderRadius: '24px',
                background: isDark ? alpha('#1e293b', 0.7) : alpha('#ffffff', 0.7),
                backdropFilter: 'blur(20px)',
              }}
            >
              <FilterSidebar
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedMetal={selectedMetal}
                setSelectedMetal={setSelectedMetal}
                priceRange={priceRange}
                setPriceRange={setPriceRange}
                categories={categories}
                metals={metals}
                minMaxPrice={minMaxPrice}
                formatPrice={formatPrice}
                clearAllFilters={clearAllFilters}
                theme={theme}
              />
            </Paper>
          </Grid>
          
          {/* Products Grid */}
          <Grid item xs={12} md={9} sx={{ maxWidth: '950px' }}>
            {/* Search and Controls Bar */}
            <Paper
              sx={{
                p: 2,
                mb: 3,
                borderRadius: '20px',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                background: isDark ? alpha('#1e293b', 0.7) : alpha('#ffffff', 0.7),
                backdropFilter: 'blur(20px)',
              }}
            >
              <TextField
                size="small"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
                sx={{ minWidth: 250, flex: 1 }}
              />
              
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Sort By</InputLabel>
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sort By">
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="price_asc">Price: Low to High</MenuItem>
                    <MenuItem value="price_desc">Price: High to Low</MenuItem>
                    <MenuItem value="weight_asc">Weight: Low to High</MenuItem>
                  </Select>
                </FormControl>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Grid View">
                    <IconButton onClick={() => setViewMode('grid')} color={viewMode === 'grid' ? 'primary' : 'default'}>
                      <ViewModuleIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="List View">
                    <IconButton onClick={() => setViewMode('list')} color={viewMode === 'list' ? 'primary' : 'default'}>
                      <ViewListIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                {isMobile && (
                  <IconButton onClick={() => setMobileFilterOpen(true)}>
                    <Badge badgeContent={selectedCategory !== 'all' || selectedMetal !== 'all' ? 1 : 0} color="primary">
                      <FilterAltIcon />
                    </Badge>
                  </IconButton>
                )}
              </Box>
            </Paper>
            
            {/* Results Count */}
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Showing {paginatedProducts.length} of {filteredProducts.length} products
            </Typography>
            
            {/* Products Grid/List */}
            <AnimatePresence mode="wait">
              {viewMode === 'grid' ? (
                <motion.div
                  key="grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Grid container spacing={3}>
                    {paginatedProducts.map((product, idx) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        index={idx}
                        formatWeight={formatWeight}
                        handleWhatsAppOrder={handleWhatsAppOrder}
                        handleShare={handleShare}
                        openQuickView={openQuickView}
                        favorites={favorites}
                        toggleFavorite={toggleFavorite}
                        isDark={isDark}
                        theme={theme}
                      />
                    ))}
                  </Grid>
                </motion.div>
              ) : (
                <motion.div
                  key="list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Stack spacing={2}>
                    {paginatedProducts.map((product, idx) => (
                      <Slide key={product.id} direction="up" in timeout={300 * idx}>
                        <Paper sx={{ p: 2, display: 'flex', gap: 2, borderRadius: '20px' }}>
                          <Box sx={{ width: 120, height: 120, borderRadius: '16px', overflow: 'hidden' }}>
                            <CardMedia
                              component="img"
                              image={product.imageUrl || 'https://via.placeholder.com/120x120'}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" fontWeight={700}>{product.name}</Typography>
                            {/* Removed Price Section */}
                            <Typography variant="body2" color="text.secondary">Serial: {product.serialNo} | {product.metal}</Typography>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<WhatsAppIcon />}
                              onClick={() => handleWhatsAppOrder(product)}
                              sx={{ mt: 1, borderRadius: '12px' }}
                            >
                              Inquire
                            </Button>
                          </Box>
                        </Paper>
                      </Slide>
                    ))}
                  </Stack>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Empty State */}
            {paginatedProducts.length === 0 && (
              <Paper sx={{ p: 8, textAlign: 'center', borderRadius: '24px' }}>
                <ShoppingBagIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h5" sx={{ mb: 1 }}>No products found</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Try adjusting your filters or search terms
                </Typography>
                <Button variant="contained" onClick={clearAllFilters}>Clear All Filters</Button>
              </Paper>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <Stack sx={{ mt: 5, alignItems: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, val) => setPage(val)}
                  color="primary"
                  size={isMobile ? "medium" : "large"}
                  showFirstButton
                  showLastButton
                />
              </Stack>
            )}
          </Grid>
        </Grid>
      </Container>
      
      {/* Mobile Filter Drawer */}
      <Drawer
        anchor="bottom"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px 24px 0 0',
            maxHeight: '85vh',
            p: 2.5,
            background: isDark ? alpha('#1e293b', 0.95) : alpha('#ffffff', 0.95),
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Filters</Typography>
          <IconButton onClick={() => setMobileFilterOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <FilterSidebar
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedMetal={selectedMetal}
            setSelectedMetal={setSelectedMetal}
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            categories={categories}
            metals={metals}
            minMaxPrice={minMaxPrice}
            formatPrice={formatPrice}
            clearAllFilters={clearAllFilters}
            theme={theme}
          />
        </Box>
        <Button fullWidth variant="contained" onClick={() => setMobileFilterOpen(false)} sx={{ mt: 2, borderRadius: '16px', py: 1.5 }}>
          Apply Filters
        </Button>
      </Drawer>
      
      {/* Quick View Modal */}
      <QuickViewModal
        quickViewOpen={quickViewOpen}
        setQuickViewOpen={setQuickViewOpen}
        selectedProduct={selectedProduct}
        formatWeight={formatWeight}
        handleWhatsAppOrder={handleWhatsAppOrder}
        handleShare={handleShare}
        isDark={isDark}
        theme={theme}
      />
    </Box>
  );
};

export default ProductGalleryPage;